import { useCallback, useEffect, useState } from 'react';

import { publish } from '../../features/EventsBus';
import { defaultTheme } from '../../features/Theme/domain';
import { DefaultTheme } from 'styled-components';
import { MeetDomainEvents, MeetRepository, Features, Tenant } from '../../features/Meet/domain';

export interface OverrideTheme {
	logo?: string;
	backgroundImage?: string;
	accentColor?: string;
	primaryColor?: string;
	secondaryColor?: string;
	chatBubbleColor?: string;
	textColor?: string;
}

const applyTheme = ({
	logo,
	chatBubbleColor,
	textColor,
	primaryColor,
	secondaryColor,
	accentColor,
	backgroundImage,
}: OverrideTheme) => {
	const hasColorToOverride =
		accentColor || primaryColor || secondaryColor || textColor || chatBubbleColor;

	return {
		...(logo && {
			logo,
		}),
		...(backgroundImage && {
			backgroundImage,
		}),
		...(hasColorToOverride && {
			colors: {
				...defaultTheme.colors,
				brand: {
					...defaultTheme.colors.brand,
					...(accentColor && {
						accent: {
							300: accentColor,
						},
					}),
					...(primaryColor && {
						primary: {
							...defaultTheme.colors.brand.primary,
							300: primaryColor,
							background: secondaryColor,
						},
					}),
					...(chatBubbleColor && {
						chatBubble: chatBubbleColor,
					}),
				},
				...(textColor && {
					text: textColor,
				}),
			},
		}),
	} as DefaultTheme;
};

export function useApp(repository: MeetRepository): {
	theme: DefaultTheme | undefined;
	loadingTheme: boolean;
	features?: Features;
} {
	const [loadingTheme, setLoadingTheme] = useState(true);
	const [theme, setTheme] = useState<DefaultTheme | undefined>(undefined);
	const [features, setFeatures] = useState<Features | undefined>(undefined);

	const loadTenantTheme = useCallback(() => {
		setLoadingTheme(true);
		repository
			.loadTenant()
			.then((tenant: Tenant) => {
				setFeatures(tenant.custom.features);
				setTheme(applyTheme(tenant.custom));
				publish(MeetDomainEvents.loadedTenant);
			})
			.catch(() => {
				publish(MeetDomainEvents.notLoadedTenant);
			})
			.finally(() => {
				setLoadingTheme(false);
			});
	}, [repository]);

	useEffect(() => {
		loadTenantTheme();
	}, [loadTenantTheme]);

	return {
		theme,
		loadingTheme,
		features,
	};
}
